import React from 'react'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Reaptcha from 'reaptcha'
import { Button } from '../Buttons/Button'
import useAppConfig from 'hooks/useAppConfig'

export interface EmailStatementDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (captchaToken: string) => void
  loading?: boolean
  physicianName?: string
}

export const EmailStatementDialog: React.FC<EmailStatementDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  physicianName,
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const siteKey = useAppConfig().additionalConfig?.siteKey ?? ''
  const [captchaToken, setCaptchaToken] = React.useState('')
  const [captchaError, setCaptchaError] = React.useState(false)
  const captchaRef = React.useRef<Reaptcha | null>(null)
  const renderRequestedRef = React.useRef(false)

  React.useEffect(() => {
    if (!open) {
      setCaptchaToken('')
      setCaptchaError(false)
      renderRequestedRef.current = false
      captchaRef.current?.reset().catch(() => undefined)
    }
  }, [open])

  const handleConfirm = () => {
    if (!captchaToken) {
      setCaptchaError(true)
      return
    }
    onConfirm(captchaToken)
  }

  const hasSiteKey = Boolean(siteKey)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="email-statement-dialog-title"
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle id="email-statement-dialog-title">
        Confirm email statement
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          To protect against automated requests, please complete the
          reCAPTCHA before sending your statement.
        </DialogContentText>
        {physicianName ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Statement recipient: {physicianName}
          </Typography>
        ) : null}
        {!hasSiteKey ? (
          <Typography color="error" variant="body2">
            reCAPTCHA is unavailable right now. Please try again later.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Reaptcha
              ref={captchaRef}
              sitekey={siteKey}
              size={fullScreen ? 'compact' : 'normal'}
              explicit
              onLoad={() => {
                if (renderRequestedRef.current) return
                renderRequestedRef.current = true
                captchaRef.current?.renderExplicitly().catch(() => undefined)
              }}
              onVerify={(token: string) => {
                setCaptchaToken(token)
                setCaptchaError(false)
              }}
              onExpire={() => {
                setCaptchaToken('')
                setCaptchaError(true)
              }}
            />
            {captchaError ? (
              <Typography color="error" variant="caption">
                Please complete the reCAPTCHA to continue.
              </Typography>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size="medium"
          variant="outlined"
          color="warning"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!hasSiteKey || loading || !captchaToken}
        >
          Send Email
        </Button>
      </DialogActions>
    </Dialog>
  )
}
